import { client } from 'apollo-client';
import format from 'date-fns/format';
import { gql } from '@apollo/client';
import { IBlogPost } from '@types';
import Markdown from 'react-markdown';
import { NextPage } from 'next';
import "highlight.js/styles/dracula.css";

import { Container } from 'Atoms/Container';
import { mdxComponents } from 'utils/mdxComponents';
import { AnimatePage } from 'Atoms/AnimatePage';
import { SeoHead } from 'Atoms/SeoHead';
import React from 'react';
import Script from 'next/script';

interface IProps {
	title: string;
	content: string;
	seoDescription: string;
	date: string;
	slug: string;
	coverImage: string;
	lastUpdated: string;
	tags?: string[];
	category?: string;
}

const PostPage: NextPage<IProps> = ({
	title,
	content,
	seoDescription,
	date,
	coverImage,
	lastUpdated,
	tags = [],
	category = "Engineering",
	slug
}) => {
	const canonicalUrl = `https://www.deepankar.online/blog/${slug}`;
	const numOfWords = content.split(' ').length;
	const readTime = Math.ceil(numOfWords / 250);

	// Structured data for Google
	const articleSchema = {
		'@context': 'https://schema.org',
		'@type': 'BlogPosting',
		headline: title,
		description: seoDescription,
		image: coverImage,
		author: {
			'@type': 'Person',
			name: 'Deepankar Sharma',
			url: 'https://www.deepankar.online'
		},
		datePublished: date,
		dateModified: lastUpdated,
		publisher: {
			'@type': 'Person',
			name: 'Deepankar Sharma',
			url: 'https://www.deepankar.online'
		},
		mainEntityOfPage: {
			'@type': 'WebPage',
			'@id': canonicalUrl
		},
		keywords: tags.join(', '),
		articleSection: category,
		wordCount: numOfWords
	};

	return (
		<AnimatePage>
			<SeoHead
				title={`${title}`}
				description={seoDescription}
				keywords={tags}
				image={coverImage}
				type="article"
				date={date}
				modifiedDate={lastUpdated}
				canonicalUrl={canonicalUrl}
				article={{
					publishedTime: date,
					modifiedTime: lastUpdated,
					tags: tags,
					section: category
				}}
			/>

			<Script
				id="article-schema"
				type="application/ld+json"
				dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
			/>

			<Container>
				<article className="h-entry">
					<h1 className="p-name headline text-3xl md:text-4xl lg:text-5xl mt-8 pb-1">
						{title}
					</h1>

					<div className="mt-4 mb-6 flex flex-row justify-between text-sm md:text-md">
						<div className='flex flex-col'>
							<time className="dt-published" dateTime={new Date(date).toISOString()}>
								Published on {format(new Date(date), 'do MMM yyyy')}
							</time>
							{lastUpdated && (
								<time className="dt-updated" dateTime={new Date(lastUpdated).toISOString()}>
									Updated on {format(new Date(lastUpdated), 'do MMM yyyy')}
								</time>
							)}
						</div>

						<em>~{readTime} minute read</em>
					</div>

					<div className="e-content">
						<Markdown components={mdxComponents}>{content}</Markdown>
					</div>
				</article>
			</Container>
		</AnimatePage>
	);
};


export async function getStaticPaths() {
	const { data } = await client.query({
		query: gql`
			query PostsQuery {
				blogs {
					slug
					title
				}
			}
		`,
	});

	return {
		paths: data.blogs.map(({ slug }: IBlogPost) => ({
			params: { slug },
		})),
		fallback: false,
	};
}

type Params = {
	params: { slug: IBlogPost['slug'] };
};

export async function getStaticProps({ params }: Params) {
	const { data } = await client.query({
		query: gql`
			query PostPageQuery($slug: String!) {
				blog(where: { slug: $slug }) {
					id
					content
					seoDescription
					date
					publishedAt
					slug
					title
					tags
					coverImage {
						url
					}
				}
			}
		`,
		variables: { slug: params.slug },
	});

	return {
		props: {
			content: data.blog.content,
			date: data.blog.date,
			slug: data.blog.slug,
			title: data.blog.title,
			seoDescription: data.blog.seoDescription,
			coverImage: data.blog.coverImage.url,
			lastUpdated: data.blog.publishedAt,
			tags: data.blog.tags
		},
	};
}

export default PostPage;
